import { useState } from "react";
import { useSelector } from "react-redux";
import { getAuth, reauthenticateWithCredential, EmailAuthProvider, updatePassword } from "firebase/auth";
import { logoutFirebase } from "../../auth/operations";
import {
  cancelCurrentMembership,
  undoCancelCurrentMembership,
} from "../../api/apiEndpoints";
import {
  getUserMembershipStatusCall,
  openBillingPortalCall,
} from "../../api/apiOperations";
import {
  Container,
  Divider,
  SectionLabel,
  Row,
  RowLabel,
  RowValue,
  ActionButton,
  LogoutRow,
} from "./styles";
import { HOME } from "../../utils/routes";
import { LogoLettersBlack } from "../../assets/images";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../utils/utils";

type MembershipAction = "cancel" | "undo";

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '11px 14px', border: '1.5px solid #e0e0e0',
  borderRadius: '12px', fontSize: '14px', fontFamily: 'Aileron, sans-serif',
  outline: 'none', boxSizing: 'border-box', marginBottom: '10px', background: '#fff',
};

const DashboardModal = () => {
  const { firstName, lastName, email, phone } = useSelector(
    (state: RootState) => state.appData.userData
  );
  const { userMembershipStatus } = useSelector((state: RootState) => state.appData);
  const { memberLoggedIn } = useSelector((state: RootState) => state.auth);

  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelError, setCancelError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [membershipAction, setMembershipAction] = useState<MembershipAction>("cancel");

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const hasActiveMembership = memberLoggedIn && userMembershipStatus?.memberships?.active;
  const scheduledCancelAt = userMembershipStatus?.settings?.scheduledCancelAt;
  const isSetToCancel = hasActiveMembership && scheduledCancelAt != null;
  const isUndoAction = membershipAction === "undo";

  const handleLogout = async () => {
    await logoutFirebase();
    navigate(HOME);
  };

  const handleOpenPasswordModal = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError(null);
    setPasswordSuccess(false);
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    if (passwordLoading) return;
    setShowPasswordModal(false);
  };

  const handlePasswordChange = async () => {
    if (newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    setPasswordLoading(true);
    setPasswordError(null);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("Not logged in");
      const credential = EmailAuthProvider.credential(email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setPasswordSuccess(true);
    } catch (err: any) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setPasswordError("Current password is incorrect.");
      } else {
        setPasswordError("Something went wrong. Please try again.");
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChangePaymentMethod = async () => {
    const url = await openBillingPortalCall();
    if (url) window.location.href = url;
    else alert("Unable to open billing portal. Please try again later.");
  };

  const handleOpenMembershipAction = () => {
    setMembershipAction(isSetToCancel ? "undo" : "cancel");
    setCancelError(false);
    setCancelled(false);
    setShowCancelModal(true);
  };

  const handleMembershipAction = async () => {
    setLoading(true);
    setCancelError(false);
    try {
      const result = isUndoAction
        ? await undoCancelCurrentMembership()
        : await cancelCurrentMembership();
      if (result?.status >= 200 && result?.status < 300) {
        setCancelled(true);
        await getUserMembershipStatusCall();
      } else {
        setCancelError(true);
      }
    } catch {
      setCancelError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Container>
        <img src={LogoLettersBlack} alt="JackedRabbit" style={{ height: 32, objectFit: 'contain', marginBottom: 18, alignSelf: 'center' }} />
        <span style={{ fontWeight: 700, fontSize: '17px', color: '#111', marginBottom: '2px' }}>
          {firstName} {lastName}
        </span>
        <span style={{ fontSize: '13px', color: '#888', marginBottom: '20px' }}>{email}</span>

        <Divider />

        <SectionLabel>Account</SectionLabel>
        <Row>
          <RowLabel>Email</RowLabel>
          <RowValue>{email}</RowValue>
        </Row>
        <Row style={{ cursor: 'pointer' }} onClick={handleOpenPasswordModal}>
          <RowLabel>Password</RowLabel>
          <ActionButton as="span">Change</ActionButton>
        </Row>
        {memberLoggedIn && (
          <Row>
            <RowLabel>Phone</RowLabel>
            <RowValue>{phone}</RowValue>
          </Row>
        )}
        {hasActiveMembership && (
          <Row>
            <RowLabel>Payment method</RowLabel>
            <ActionButton onClick={handleChangePaymentMethod}>Update</ActionButton>
          </Row>
        )}

        <Divider style={{ marginTop: 8 }} />

        <SectionLabel>Support</SectionLabel>
        <Row style={{ cursor: 'pointer' }} onClick={() => { window.location.href = 'mailto:support@jackedrabbitgyms.com'; }}>
          <RowLabel>Ask for help</RowLabel>
          <ActionButton as="span">Contact us</ActionButton>
        </Row>

        {hasActiveMembership && (
          <>
            <Divider style={{ marginTop: 8 }} />
            <SectionLabel>Membership</SectionLabel>
            <Row style={{ cursor: 'pointer' }} onClick={handleOpenMembershipAction}>
              <RowLabel>
                {isSetToCancel ? "Remain a member" : "Cancel subscription"}
              </RowLabel>
              <ActionButton
                as="span"
                danger={!isSetToCancel}
                style={isSetToCancel ? {
                  backgroundColor: '#1a1a1a',
                  color: '#ffffff',
                  border: '1.5px solid #1a1a1a',
                  borderRadius: '999px',
                  padding: '7px 14px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.06)',
                } : undefined}
              >
                {isSetToCancel ? "Stay active" : "Cancel"}
              </ActionButton>
            </Row>
          </>
        )}

        <LogoutRow onClick={handleLogout}>Log out</LogoutRow>
      </Container>

      {/* Password change modal */}
      {showPasswordModal && (
        <div onClick={handleClosePasswordModal} style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: '#faf6f1', borderRadius: '24px', padding: '32px 24px',
            maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          }}>
            <div style={{ width: '40px', height: '4px', borderRadius: '2px', backgroundColor: '#d4cbc2', margin: '0 auto 24px' }} />

            {passwordSuccess ? (
              <>
                <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#1a1a1a', margin: '0 0 10px' }}>Password updated</h3>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: 1.6, margin: '0 0 24px' }}>
                  Your password has been changed successfully.
                </p>
                <div style={{ height: '1px', backgroundColor: '#e8e2dc', marginBottom: '20px' }} />
                <button onClick={handleClosePasswordModal} style={{
                  width: '100%', backgroundColor: '#1a1a1a', color: '#fff', border: 'none',
                  borderRadius: '30px', padding: '15px', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
                }}>Done</button>
              </>
            ) : (
              <>
                <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#1a1a1a', margin: '0 0 6px', letterSpacing: '-0.3px' }}>Change password</h3>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: 1.6, margin: '0 0 20px' }}>
                  Enter your current password, then choose a new one.
                </p>
                <div style={{ height: '1px', backgroundColor: '#e8e2dc', marginBottom: '20px' }} />
                <input
                  type="password"
                  placeholder="Current password"
                  value={currentPassword}
                  onChange={e => { setCurrentPassword(e.target.value); setPasswordError(null); }}
                  style={inputStyle}
                  autoComplete="current-password"
                />
                <input
                  type="password"
                  placeholder="New password (min. 6 characters)"
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); setPasswordError(null); }}
                  style={inputStyle}
                  autoComplete="new-password"
                />
                <input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); setPasswordError(null); }}
                  style={{ ...inputStyle, marginBottom: passwordError ? 8 : 20 }}
                  autoComplete="new-password"
                />
                {passwordError && (
                  <p style={{ fontSize: '13px', color: '#dc2626', margin: '0 0 16px', lineHeight: 1.4 }}>{passwordError}</p>
                )}
                <button
                  onClick={handlePasswordChange}
                  disabled={!currentPassword || !newPassword || !confirmPassword || passwordLoading}
                  style={{
                    width: '100%', backgroundColor: '#1a1a1a', color: '#fff', border: 'none',
                    borderRadius: '30px', padding: '15px', fontSize: '15px', fontWeight: 700,
                    cursor: passwordLoading || !currentPassword || !newPassword || !confirmPassword ? 'not-allowed' : 'pointer',
                    marginBottom: '10px', opacity: passwordLoading || !currentPassword || !newPassword || !confirmPassword ? 0.5 : 1,
                  }}
                >
                  {passwordLoading ? 'Updating…' : 'Update password'}
                </button>
                <button onClick={handleClosePasswordModal} disabled={passwordLoading} style={{
                  width: '100%', backgroundColor: 'transparent', color: '#1a1a1a', border: '1.5px solid #1a1a1a',
                  borderRadius: '30px', padding: '14px', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                }}>Cancel</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Cancel confirmation modal */}
      {showCancelModal && (
        <div onClick={() => !loading && setShowCancelModal(false)} style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: '#faf6f1', borderRadius: '24px', padding: '32px 24px',
            maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          }}>
            <div style={{ width: '40px', height: '4px', borderRadius: '2px', backgroundColor: '#d4cbc2', margin: '0 auto 24px' }} />

            {cancelled ? (
              <>
                <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#1a1a1a', margin: '0 0 10px', letterSpacing: '-0.3px' }}>
                  {isUndoAction ? "Cancellation reversed" : "Subscription cancelled"}
                </h3>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: 1.6, margin: '0 0 24px' }}>
                  {isUndoAction
                    ? "Your membership will remain active and will not be canceled at the end of this billing cycle."
                    : "Your membership has been scheduled for cancellation. You'll retain access until the end of your current billing period."}
                </p>
                <div style={{ height: '1px', backgroundColor: '#e8e2dc', marginBottom: '20px' }} />
                <button onClick={() => setShowCancelModal(false)} style={{
                  width: '100%', backgroundColor: '#1a1a1a', color: '#fff', border: 'none',
                  borderRadius: '30px', padding: '15px', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
                }}>Done</button>
              </>
            ) : cancelError ? (
              <>
                <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#1a1a1a', margin: '0 0 10px' }}>Something went wrong</h3>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: 1.6, margin: '0 0 24px' }}>
                  We couldn't process your request. Please try again or contact support.
                </p>
                <div style={{ height: '1px', backgroundColor: '#e8e2dc', marginBottom: '20px' }} />
                <button onClick={() => setCancelError(false)} style={{
                  width: '100%', backgroundColor: '#1a1a1a', color: '#fff', border: 'none',
                  borderRadius: '30px', padding: '15px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', marginBottom: '10px',
                }}>Try again</button>
                <button onClick={() => setShowCancelModal(false)} style={{
                  width: '100%', backgroundColor: 'transparent', color: '#1a1a1a', border: '1.5px solid #1a1a1a',
                  borderRadius: '30px', padding: '14px', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                }}>Go back</button>
              </>
            ) : (
              <>
                <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#1a1a1a', margin: '0 0 10px', letterSpacing: '-0.3px' }}>
                  {isUndoAction ? "Keep your membership active?" : "Cancel subscription?"}
                </h3>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: 1.6, margin: '0 0 24px' }}>
                  {isUndoAction
                    ? `This will undo your scheduled cancellation${scheduledCancelAt ? ` on ${formatDate(scheduledCancelAt)}` : ""}. Your membership will continue as normal.`
                    : "You'll lose access to all JackedRabbit gyms at the end of your current billing period."}
                </p>
                <div style={{ height: '1px', backgroundColor: '#e8e2dc', marginBottom: '20px' }} />
                <button onClick={handleMembershipAction} disabled={loading} style={{
                  width: '100%',
                  backgroundColor: isUndoAction ? '#1a1a1a' : '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '30px', padding: '15px', fontSize: '15px', fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer', marginBottom: '10px', opacity: loading ? 0.6 : 1,
                  boxShadow: isUndoAction ? '0 10px 22px rgba(0, 0, 0, 0.14)' : 'none',
                }}>
                  {loading ? 'Processing…' : isUndoAction ? 'Remain a member' : 'Yes, cancel subscription'}
                </button>
                <button onClick={() => setShowCancelModal(false)} disabled={loading} style={{
                  width: '100%', backgroundColor: 'transparent', color: '#1a1a1a', border: '1.5px solid #1a1a1a',
                  borderRadius: '30px', padding: '14px', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                }}>{isUndoAction ? "Go back" : "Keep my membership"}</button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardModal;
